import { lineString, featureCollection, polygon, point } from '@turf/helpers';
import intersect from '@turf/intersect';
import area from '@turf/area';
import centroid from '@turf/centroid';
import flatten from '@turf/flatten';
import buffer from '@turf/buffer';
import pointsWithinPolygon from '@turf/points-within-polygon';
import * as topojson from 'topojson-client';
import concaveman from 'concaveman';
import cut from './turf-cut';

import config from './config';

export function getResultData(state) {
  const { drawCoords, outlineGeoJson, kreiseGeoJson, kreiseTopoJson } = state;

  const coords = drawCoords.map((coord) => [coord.lng, coord.lat]);

  if (coords.length < 2) {
    throw new Error('errorShortLine');
  }

  const drawLineString = lineString(coords);
  const outlineCuts = cut(outlineGeoJson, drawLineString);

  if (outlineCuts.features.length !== 2) {
    throw new Error('errorFeatureCount');
  }

  const splitFeatures = kreiseGeoJson.features.reduce((res, feat) => {
    const intersections = [];

    outlineCuts.features.forEach((cut, i) => {
      const intersection = intersect(feat, cut);

      if (intersection) {
        intersections.push({ index: i, polygon: intersection });
      }
    });

    if (intersections.length === 1) {
      const index = intersections[0].index;
      res[index] = res[index] ? res[index].concat([feat]) : [feat];
    }

    if (intersections.length === 2) {
      const areas = intersections
        .map((intersection, i) => {
          const size = area(intersection.polygon);
          return { ...intersection, size };
        })
        .sort((a, b) => b.size - a.size);

      const index = areas[0].index;
      res[index] = res[index] ? res[index].concat([feat]) : [feat];
    }

    return res;
  }, []);

  if (splitFeatures.length < 2) {
    throw new Error('errorFeatureCount');
  }

  const result = getResultFromFeatures(splitFeatures, kreiseTopoJson);
  return result;
}

function getOutlinePolygon(geoData, topoData) {
  const ids = geoData.features.map((feat) => feat.properties.__id);
  const topoKey = Object.keys(topoData.objects)[0];
  return topojson.merge(
    topoData,
    topoData.objects[topoKey].geometries.filter((geom) =>
      ids.includes(geom.properties.__id)
    )
  );
}

function getLabelsInsidePolygon(labels, polygon) {
  const points = labels.map((l) => point([l[2], l[1]], { d: l }));
  const fcPoints = featureCollection(points);
  const filteredPoints = pointsWithinPolygon(fcPoints, polygon);

  return filteredPoints.features.map((p) => p.properties.d);
}

function getResultFromFeatures(features, allTopo, presetName = false) {
  const shapes = features.map((feats, i) => {
    const geojson = featureCollection(feats);
    const center = centroid(geojson).geometry.coordinates.reverse();
    const outline = getOutlinePolygon(geojson, allTopo);
    const labels = getLabelsInsidePolygon(config.mapLabels, outline);
    const bufferedOutline = buffer(outline, 9, { steps: 32 });

    return {
      geojson,
      center,
      moveDir: i === 0 ? 1 : -1,
      outline,
      labels,
      bufferedOutline,
      presetName,
    };
  });

  return {
    shapes,
    id: Date.now(),
  };
}

function getPresetFilter(preset) {
  if (preset === 'bavaria') {
    return (feat) => feat.properties.bl === '09';
  }
  if (preset === 'northsouth') {
    return (feat) =>
      ['01', '02', '03', '04', '05', '11', '12', '13', '15'].includes(
        feat.properties.bl
      );
  }
  if (preset === 'eastwest') {
    return (feat) =>
      ['11', '12', '13', '14', '15', '16'].includes(feat.properties.bl);
  }
  return (feat) => feat.properties.bl === '08';
}

export function getPresetResult(preset, state) {
  const { features } = state.kreiseGeoJson;
  const filter = getPresetFilter(preset);

  const splitFeatures = features.reduce((res, feat) => {
    if (filter(feat)) {
      res[0] = res[0] ? res[0].concat([feat]) : [feat];
    } else {
      res[1] = res[1] ? res[1].concat([feat]) : [feat];
    }
    return res;
  }, []);

  const result = getResultFromFeatures(
    splitFeatures,
    state.kreiseTopoJson,
    preset
  );
  return result;
}

export function getMoveAngle(shapes, map) {
  const start = map.project(shapes[0].center);
  const end = map.project(shapes[1].center);
  const dx = start.x - end.x;
  const dy = start.y - end.y;
  const theta = Math.atan2(dy, dx);
  return theta;
}

export function getOutlineConcave(geojson, threshold = 2) {
  const points = geojson.features.reduce((coords, feat) => {
    const flat = flatten(feat);

    flat.features.forEach((f) => {
      coords = coords.concat(f.geometry.coordinates[0]);
    });

    return coords;
  }, []);

  const concave = concaveman(points, threshold);
  return polygon([concave]);
}

export function dissolve(topoData, property) {
  const key = Object.keys(topoData.objects)[0];
  const topoObject = topoData.objects[key];
  const groups = topoObject.geometries
    .reduce((res, item) => {
      const currentValue = item.properties[property];

      if (!res.includes(currentValue)) {
        res.push(currentValue);
      }

      return res;
    }, [])
    .map((value) =>
      topojson.merge(
        topoData,
        topoObject.geometries.filter((d) => d.properties[property] === value)
      )
    );

  return groups;
}

export function distance(a, b) {
  return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));
}

export function nearest(point, points) {
  return points.reduce((a, b) =>
    distance(point, a) < distance(point, b) ? a : b
  );
}

export default {
  getResultData,
  getMoveAngle,
  getOutlinePolygon,
  getOutlineConcave,
  dissolve,
  distance,
  nearest,
};
